import {
	type Icon,
	IconAlignLeft,
	IconArrowBackUpDouble,
	IconCancel,
	IconCertificate2,
	IconCopy,
	IconDots,
	IconDownload,
	IconFileDescription,
	IconFileText,
	IconFileUpload,
	IconLink,
	IconListCheck,
	IconMessage2,
	IconPaperclip,
	IconPlayerPlay,
	IconPlus,
	IconRefresh,
	IconReload,
	IconSend,
	IconTemplate,
	IconTrash,
} from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Alert, Button, Card, Flex, Input, Modal, Tabs, theme } from "antd";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { useAgent } from "@/common/artemis-operations/agents";
import { useApprovals } from "@/common/artemis-operations/approvals";
import { useDeleteAssets } from "@/common/artemis-operations/assets";
import {
	useCancelTask,
	useDeleteTasks,
	useDispatchTask,
	useReopenTask,
	useSubmitTaskInput,
	useTask,
	useUpdateTask,
} from "@/common/artemis-operations/tasks";
import type { ResourceLinkInput } from "@/common/artemis-sdk";
import { downloadObjectAsMarkdown, slugify } from "@/common/markdown";
import {
	DELETE_RESOURCE_DESC,
	DELETE_RESOURCE_ERROR_FAILED,
	DELETE_RESOURCE_INFO_COMPLETED,
	DELETE_RESOURCE_TITLE,
	ERROR_TASK_CANCEL_DESC,
	ERROR_TASK_CANCEL_TITLE,
	ERROR_TASK_DISPATCH_DESC,
	ERROR_TASK_DISPATCH_TITLE,
	ERROR_TASK_INPUT_SUBMIT_DESC,
	ERROR_TASK_INPUT_SUBMIT_TITLE,
	ERROR_TASK_REOPEN_DESC,
	ERROR_TASK_REOPEN_TITLE,
	TASK_CANCEL_DESC,
	TASK_CANCEL_TITLE,
	TASK_CANCELLED_INFO_COMPLETED,
	TASK_DISPATCHED_INFO_COMPLETED,
	TASK_INPUT_SUBMITTED_INFO_COMPLETED,
	TASK_REOPENED_INFO_COMPLETED,
	UPDATE_RESOURCE_ERROR_FAILED,
	UPDATE_RESOURCE_INFO_COMPLETED,
} from "@/common/messages";
import {
	type Agent,
	type ArtemisTask,
	type Asset,
	AssetCategory,
	AssigneeType,
	agentIsDispatchable,
	ParentType,
	TaskStatus,
} from "@/common/model";
import { GraphQLRequestError } from "@/common/use-gql-client";
import { useNotify } from "@/common/use-notify";
import { ApprovalTable } from "@/components/approvals/approval-table";
import { AssetTable } from "@/components/assets/asset-table";
import { AssetViewerDrawer } from "@/components/assets/asset-viewer-drawer";
import { UploadAssetDialog } from "@/components/assets/upload-asset-dialog";
import {
	BaseContainer,
	BasePageContainer,
	CustomEmpty,
	CustomIcon,
	QueryGuard,
} from "@/components/base";
import { DocumentEditor } from "@/components/document-editor";
import { DocumentEditorDrawer } from "@/components/document-editor-drawer";
import { useFrictionConfirm } from "@/components/friction-confirm-dialog";
import { AddLinkModal } from "@/components/projects/add-link-modal";
import { ProvideInputModal } from "@/components/tasks/provide-input-modal";
import {
	TaskDetails,
	TaskLinks,
	VerificationDetails,
} from "@/components/tasks/task-details";
import { useActions } from "@/contexts/actions-context";
import { usePageTitle } from "@/contexts/page-title-context";
import { useWizards } from "@/contexts/wizard-context";

export const Route = createFileRoute(
	"/_authenticated/$orgSlug/workspaces/$workspaceId/tasks/$taskId",
)({ component: TaskPage });

const DISPATCHABLE_TASK_STATUSES: TaskStatus[] = [
	TaskStatus.CREATED,
	TaskStatus.WAITING,
	TaskStatus.INPUT_REQUIRED,
];

const STOPPABLE_TASK_STATUSES: TaskStatus[] = [
	TaskStatus.WAITING,
	TaskStatus.WORKING,
];

// Terminal statuses a task can be reopened from — mirrors the backend guard in ReopenTaskAsync.
const REOPENABLE_TASK_STATUSES: TaskStatus[] = [
	TaskStatus.COMPLETED,
	TaskStatus.FAILED,
	TaskStatus.CANCELLED,
];

// An assignee with no A2A endpoint can never be reached or tracked once dispatched (mirrors the
// backend guard in DispatchTaskAsync). Skipped while `agent` hasn't loaded yet, not treated as
// missing — otherwise the button would flash disabled before the agent query resolves.
function taskCanBeStarted(
	task: ArtemisTask | undefined,
	agent: Agent | null | undefined,
): boolean {
	if (task?.assigneeType !== AssigneeType.AGENT) return false;
	if (!task?.assigneeId) return false;
	if (agent && !agentIsDispatchable(agent)) return false;

	return task != null && DISPATCHABLE_TASK_STATUSES.includes(task.status);
}

function taskCanBeStopped(task: ArtemisTask | undefined): boolean {
	if (task?.assigneeType !== AssigneeType.AGENT) return false;
	if (!task?.assigneeId) return false;

	return task != null && STOPPABLE_TASK_STATUSES.includes(task.status);
}

function taskCanBeReopened(task: ArtemisTask | undefined): boolean {
	if (task?.assigneeType !== AssigneeType.AGENT) return false;
	if (!task?.assigneeId) return false;

	return task != null && REOPENABLE_TASK_STATUSES.includes(task.status);
}

/** Tab label with a trailing count when non-empty. */
const tabLabel = (name: string, count: number) =>
	count > 0 ? `${name} (${count})` : name;

/** Tab label with a leading icon and an optional trailing count. */
const tabLabelWithIcon = (
	icon: React.ComponentType<React.ComponentPropsWithoutRef<Icon>>,
	name: string,
	count?: number,
) => (
	<Flex align="center" gap={6}>
		<CustomIcon icon={icon} size={16} />
		{count === undefined ? name : tabLabel(name, count)}
	</Flex>
);

function TaskPage() {
	const { orgSlug, taskId, workspaceId } = Route.useParams();
	const navigate = useNavigate();
	const { setTitle } = usePageTitle();
	const { setActions } = useActions();
	const { openTaskCreateWizard, openTaskTemplateWizard } = useWizards();
	const notify = useNotify();
	const { data: task, isPending, isError, refetch } = useTask(taskId);
	// ponytail: workspace approvals filtered client-side by taskId; add a
	// taskId-filtered query if a workspace ever has too many approvals to fetch.
	const { data: workspaceApprovals, refetch: refetchApprovals } =
		useApprovals(workspaceId);
	const taskApprovals = workspaceApprovals?.filter(
		(a): a is NonNullable<typeof a> => a?.taskId === taskId,
	);
	const updateTask = useUpdateTask(taskId);
	const { mutate: deleteTasks } = useDeleteTasks();
	const cancelTask = useCancelTask(taskId);
	const submitTaskInput = useSubmitTaskInput(taskId);
	const { dispatchTask } = useDispatchTask(taskId);
	const { data: assigneeAgent } = useAgent(task?.assigneeId ?? "", workspaceId);
	const reopenTask = useReopenTask(taskId);
	// Mirrors the backend guard in SubmitTaskInputAsync: a working task is busy, and a terminal one
	// is over — its input could never be delivered. The follow-up composer is the way to answer a
	// finished task; it reopens first. (REOPENABLE_TASK_STATUSES is the terminal set.)
	const acceptInput =
		task != null &&
		task.status !== TaskStatus.WORKING &&
		!REOPENABLE_TASK_STATUSES.includes(task.status);
	const [addLinkOpen, setAddLinkOpen] = useState(false);
	const [instructionsOpen, setInstructionsOpen] = useState(false);
	const [provideInputOpen, setProvideInputOpen] = useState(false);
	const [followUp, setFollowUp] = useState("");
	const [uploadAssetOpen, setUploadAssetOpen] = useState(false);
	const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
	const { mutate: deleteAssets } = useDeleteAssets();
	const queryClient = useQueryClient();
	const { confirm, dialog } = useFrictionConfirm();

	const {
		token: { colorBgContainerDisabled },
	} = theme.useToken();

	const save = (
		fields: Partial<{
			name: string;
			assigneeId: string;
			assigneeType: AssigneeType;
			description: string;
			instructions: string;
			links: ResourceLinkInput[];
		}>,
	) =>
		updateTask(
			{
				name: task?.name ?? "",
				assigneeId: task?.assigneeId ?? "",
				description: task?.description ?? "",
				instructions: task?.instructions ?? "",
				...fields,
			},
			{
				onSuccess: () => notify.success(UPDATE_RESOURCE_INFO_COMPLETED),
				onError: () => notify.error(UPDATE_RESOURCE_ERROR_FAILED),
			},
		);

	const downloadRef = useRef(() => {});
	downloadRef.current = () => {
		if (!task) return;
		const body = [
			task.description && `## Description\n\n${task.description}`,
			task.instructions && `## Instructions\n\n${task.instructions}`,
			task.result && `## Output\n\n${task.result}`,
		]
			.filter(Boolean)
			.join("\n\n");
		downloadObjectAsMarkdown(
			task as unknown as Record<string, unknown>,
			slugify(task.name),
			{
				title: task.name,
				body,
				exclude: [
					"__typename",
					"assets",
					"links",
					"worklog",
					"metrics",
					"description",
					"instructions",
					"result",
				],
			},
		);
	};

	// Duplicate = open the create wizard pre-filled with the definition fields; run state
	// (status, result, assets, worklog) and links are not copied.
	const duplicateRef = useRef(() => {});
	duplicateRef.current = () => {
		if (!task) return;
		openTaskCreateWizard({
			name: `${task.name} (copy)`,
			description: task.description ?? "",
			instructions: task.instructions,
			projectId: task.projectId,
			deploymentIds: task.deploymentIds,
			environmentIds: task.environmentIds,
			assigneeId: task.assigneeId,
			assigneeType: task.assigneeType,
			verificationCriteria: task.verificationCriteria,
		});
	};

	// Save as template = the same definition-field copy as Duplicate, but stored as a
	// reusable template instead of creating a task. The name is kept verbatim.
	const saveAsTemplateRef = useRef(() => {});
	saveAsTemplateRef.current = () => {
		if (!task) return;
		openTaskTemplateWizard({
			name: task.name,
			description: task.description ?? "",
			instructions: task.instructions,
			projectId: task.projectId,
			deploymentIds: task.deploymentIds,
			environmentIds: task.environmentIds,
			assigneeId: task.assigneeId,
			assigneeType: task.assigneeType,
			verificationCriteria: task.verificationCriteria,
		});
	};

	const confirmDeleteRef = useRef(() => {});
	confirmDeleteRef.current = () => {
		confirm({
			title: DELETE_RESOURCE_TITLE,
			message: DELETE_RESOURCE_DESC,
			onConfirm: () => {
				deleteTasks([taskId], {
					onSuccess: () => {
						notify.success(DELETE_RESOURCE_INFO_COMPLETED);
						queryClient.invalidateQueries({ queryKey: ["task", taskId] });
						navigate({
							to: "/$orgSlug/workspaces/$workspaceId/tasks",
							params: { orgSlug, workspaceId },
						});
					},
					onError: () => notify.error(DELETE_RESOURCE_ERROR_FAILED),
				});
			},
		});
	};

	const dispatchRef = useRef(() => {});
	dispatchRef.current = () => {
		dispatchTask(undefined, {
			onSuccess: () => notify.success(TASK_DISPATCHED_INFO_COMPLETED),
			onError: (error) =>
				notify.error(
					ERROR_TASK_DISPATCH_TITLE,
					// Server-side dispatch preconditions (e.g. no A2A endpoint configured) are
					// user-actionable — show the real reason instead of a generic retry prompt.
					error instanceof GraphQLRequestError
						? error.message
						: ERROR_TASK_DISPATCH_DESC,
				),
		});
	};

	const reopenRef = useRef(() => {});
	reopenRef.current = () => {
		reopenTask(undefined, {
			onSuccess: () => notify.success(TASK_REOPENED_INFO_COMPLETED),
			onError: () =>
				notify.error(ERROR_TASK_REOPEN_TITLE, ERROR_TASK_REOPEN_DESC),
		});
	};

	// Rerun = reopen (reset to CREATED, clear stale run state) then dispatch, chained so the
	// dispatch only fires once the reset is persisted.
	const rerunRef = useRef(() => {});
	rerunRef.current = () => {
		reopenTask(undefined, {
			onSuccess: () => dispatchRef.current(),
			onError: () =>
				notify.error(ERROR_TASK_REOPEN_TITLE, ERROR_TASK_REOPEN_DESC),
		});
	};

	// Chat-style follow-up: submit the text as task input and restart in one action.
	// Terminal tasks are reopened first (reset to CREATED), then input + dispatch.
	const sendFollowUpRef = useRef((_text: string) => {});
	sendFollowUpRef.current = (text: string) => {
		const submitAndDispatch = () =>
			submitTaskInput(text, {
				onSuccess: () => dispatchRef.current(),
				onError: () =>
					notify.error(
						ERROR_TASK_INPUT_SUBMIT_TITLE,
						ERROR_TASK_INPUT_SUBMIT_DESC,
					),
			});
		if (taskCanBeReopened(task)) {
			reopenTask(undefined, {
				onSuccess: submitAndDispatch,
				onError: () =>
					notify.error(ERROR_TASK_REOPEN_TITLE, ERROR_TASK_REOPEN_DESC),
			});
		} else {
			submitAndDispatch();
		}
	};

	const confirmCancelRef = useRef(() => {});
	confirmCancelRef.current = () => {
		Modal.confirm({
			title: TASK_CANCEL_TITLE,
			content: TASK_CANCEL_DESC,
			onOk() {
				cancelTask(undefined, {
					onSuccess: () => notify.success(TASK_CANCELLED_INFO_COMPLETED),
					onError: () =>
						notify.error(ERROR_TASK_CANCEL_TITLE, ERROR_TASK_CANCEL_DESC),
				});
			},
		});
	};

	useEffect(() => {
		setTitle(task?.name ?? "Task");
	}, [setTitle, task]);

	useEffect(() => {
		setActions([
			{
				type: "button",
				id: "dispatch",
				disabled: !taskCanBeStarted(task, assigneeAgent),
				tooltip:
					assigneeAgent && !agentIsDispatchable(assigneeAgent)
						? "Agent has no A2A endpoint URL configured"
						: "Start task",
				icon: <CustomIcon icon={IconPlayerPlay} />,
				onClick: () => dispatchRef.current(),
			},
			{
				type: "button",
				id: "cancel",
				danger: false,
				tooltip: "Cancel task",
				disabled: !taskCanBeStopped(task),
				icon: <CustomIcon icon={IconCancel} />,
				onClick: () => confirmCancelRef.current(),
			},
			{
				type: "button",
				id: "instructions",
				tooltip: "Agent instructions",
				icon: <CustomIcon icon={IconAlignLeft} />,
				onClick: () => setInstructionsOpen(true),
			},
			...(acceptInput
				? [
						{
							type: "button" as const,
							id: "provide-input",
							tooltip: "Provide input",
							icon: <CustomIcon icon={IconMessage2} />,
							onClick: () => setProvideInputOpen(true),
						},
					]
				: []),
			{
				type: "submenu",
				id: "add",
				icon: <CustomIcon icon={IconPlus} />,
				submenu: [
					{
						type: "button",
						id: "add-file",
						label: "File",
						icon: <CustomIcon icon={IconFileUpload} />,
						onClick: () => setUploadAssetOpen(true),
					},
					{
						type: "button",
						id: "add-link",
						label: "Link",
						icon: <CustomIcon icon={IconLink} />,
						onClick: () => setAddLinkOpen(true),
					},
				],
			},
			{
				type: "submenu",
				id: "settings",
				icon: <CustomIcon icon={IconDots} />,
				submenu: [
					{
						type: "button",
						id: "rerun",
						label: "Restart",
						tooltip: "Restart task",
						disabled: !taskCanBeReopened(task),
						icon: <CustomIcon icon={IconReload} />,
						onClick: () => rerunRef.current(),
					},
					{
						type: "button",
						id: "reopen",
						tooltip: "Reopen task (reset to Created)",
						label: "Reopen task",
						disabled: !taskCanBeReopened(task),
						icon: <CustomIcon icon={IconArrowBackUpDouble} />,
						onClick: () => reopenRef.current(),
					},
					{
						type: "button",
						id: "duplicate",
						label: "Duplicate",
						tooltip: "Create a new task with the same fields",
						icon: <CustomIcon icon={IconCopy} />,
						onClick: () => duplicateRef.current(),
					},
					{
						type: "button",
						id: "save-as-template",
						label: "Save as template",
						tooltip: "Save these fields as a reusable task template",
						icon: <CustomIcon icon={IconTemplate} />,
						onClick: () => saveAsTemplateRef.current(),
					},
					{
						type: "button",
						id: "download",
						label: "Export",
						tooltip: "Download as Markdown",
						icon: <CustomIcon icon={IconDownload} />,
						onClick: () => downloadRef.current(),
					},
					{
						type: "button",
						id: "refresh",
						label: "Refresh",
						icon: <CustomIcon icon={IconRefresh} />,
						onClick: () => {
							refetch();
							refetchApprovals();
						},
					},
					{
						type: "button",
						id: "delete",
						danger: true,
						label: "Delete",
						icon: <CustomIcon icon={IconTrash} />,
						onClick: () => confirmDeleteRef.current(),
					},
				],
			},
		]);
		return () => setActions([]);
	}, [setActions, refetch, refetchApprovals, acceptInput, task, assigneeAgent]);

	const handleAddLink = (link: ResourceLinkInput) => {
		save({
			links: [
				...(task?.links ?? []).map((l) => ({
					url: l.url,
					name: l.name,
					description: l.description,
					type: l.type,
				})),
				link,
			],
		});
	};

	// Loading / network error / "task": null all render through the guard here: everything below
	// dereferences `task` eagerly, so it can't be wrapped around the JSX further down.
	if (isPending || isError || !task) {
		return (
			<QueryGuard isPending={isPending} isError={isError} isNull={!task}>
				{null}
			</QueryGuard>
		);
	}

	const allAssets = task.assets ?? [];
	// Platform-generated per-run transcripts are surfaced in their own tab (oldest→newest = the
	// run-by-run continuation) and kept out of the user-facing Files tab.
	const transcriptAssets = allAssets
		.filter((a) => a.category === AssetCategory.TRANSCRIPT)
		.sort((a, b) => String(a.createdAt).localeCompare(String(b.createdAt)));
	const assets = allAssets.filter(
		(a) => a.category !== AssetCategory.TRANSCRIPT,
	);
	const linksCount = task.links?.length ?? 0;
	const approvals = taskApprovals ?? [];

	const tabItems = [
		{
			key: "output",
			label: tabLabelWithIcon(IconFileText, "Output"),
			children: (
				<Flex vertical gap={16}>
					{task.result ? (
						<Card
							style={{
								backgroundColor: colorBgContainerDisabled,
								width: "100%",
							}}
						>
							<DocumentEditor
								content={task.result}
								readOnly
								viewMode="preview"
							/>
						</Card>
					) : (
						<CustomEmpty description="output" />
					)}
					{(taskCanBeStarted(task, assigneeAgent) ||
						taskCanBeReopened(task)) && (
						<Flex gap={8} align="flex-end">
							<Input.TextArea
								autoSize={{ minRows: 1, maxRows: 6 }}
								placeholder="Send a follow-up and restart the task"
								value={followUp}
								onChange={(e) => setFollowUp(e.target.value)}
							/>
							<Button
								type="primary"
								icon={<CustomIcon icon={IconSend} />}
								disabled={!followUp.trim()}
								onClick={() => {
									sendFollowUpRef.current(followUp.trim());
									setFollowUp("");
								}}
							>
								Send
							</Button>
						</Flex>
					)}
				</Flex>
			),
		},

		{
			key: "verification",
			label: tabLabelWithIcon(IconListCheck, "Verification"),
			children: <VerificationDetails task={task} />,
		},
		{
			key: "summary",
			label: tabLabelWithIcon(IconFileDescription, "Summary"),
			children: task.summary ? (
				<Card
					style={{ backgroundColor: colorBgContainerDisabled, width: "100%" }}
				>
					<DocumentEditor content={task.summary} readOnly viewMode="preview" />
				</Card>
			) : (
				<CustomEmpty description="summary" />
			),
		},
		{
			key: "transcript",
			label: tabLabelWithIcon(
				IconMessage2,
				"Sessions",
				transcriptAssets.length,
			),
			children:
				transcriptAssets.length > 0 ? (
					<AssetTable
						data={transcriptAssets}
						onViewAsset={(asset) => setSelectedAsset(asset)}
					/>
				) : (
					<CustomEmpty description="transcript" />
				),
		},
		{
			key: "assets",
			label: tabLabelWithIcon(IconPaperclip, "Assets", assets.length),
			children:
				assets.length > 0 ? (
					<AssetTable
						data={assets}
						onViewAsset={(asset) => setSelectedAsset(asset)}
						onDeleteAssets={(assetIds) =>
							deleteAssets(
								assetIds.map((a) => a.id),
								{
									onSuccess: () => {
										queryClient.invalidateQueries({
											queryKey: ["task", taskId],
										});
										notify.success(DELETE_RESOURCE_INFO_COMPLETED);
									},
									onError: () => notify.error(DELETE_RESOURCE_ERROR_FAILED),
								},
							)
						}
					/>
				) : (
					<CustomEmpty
						description="Assets"
						onAdd={() => setUploadAssetOpen(true)}
					/>
				),
		},
		{
			key: "links",
			label: tabLabelWithIcon(IconLink, "Links", linksCount),
			children: <TaskLinks task={task} onAdd={() => setAddLinkOpen(true)} />,
		},

		{
			key: "approvals",
			label: tabLabelWithIcon(IconCertificate2, "Approvals", approvals.length),
			children: (
				<ApprovalTable
					workspaceId={workspaceId}
					data={approvals}
					// The list is already scoped to this task, so the workspace toolbar is noise;
					// the page's own Refresh action reloads the approvals too.
					filterBar={undefined}
					onRefresh={undefined}
				/>
			),
		},
	];

	return (
		<>
			<BasePageContainer>
				<Flex vertical gap={24}>
					{task.status === TaskStatus.INPUT_REQUIRED && (
						<Alert
							type="warning"
							description="This task needs your input to continue."
							action={
								<Button
									size="small"
									type="primary"
									onClick={() => setProvideInputOpen(true)}
								>
									Provide input
								</Button>
							}
						/>
					)}
					<BaseContainer title="Details">
						<TaskDetails
							task={task}
							workspaceId={workspaceId}
							onSaveName={(name) => save({ name })}
							onSaveDescription={(description) => save({ description })}
							onSaveAssignee={(id, type) =>
								save({ assigneeId: id, assigneeType: type })
							}
						/>
					</BaseContainer>

					<BaseContainer>
						<Tabs
							tabPlacement="top"
							defaultActiveKey="output"
							items={tabItems}
						/>
					</BaseContainer>
				</Flex>
			</BasePageContainer>

			<AddLinkModal
				open={addLinkOpen}
				onClose={() => setAddLinkOpen(false)}
				onAdd={handleAddLink}
			/>

			<ProvideInputModal
				open={provideInputOpen}
				onClose={() => setProvideInputOpen(false)}
				onSubmit={(text) =>
					submitTaskInput(text, {
						onSuccess: () =>
							notify.success(TASK_INPUT_SUBMITTED_INFO_COMPLETED),
						onError: () =>
							notify.error(
								ERROR_TASK_INPUT_SUBMIT_TITLE,
								ERROR_TASK_INPUT_SUBMIT_DESC,
							),
					})
				}
			/>

			<DocumentEditorDrawer
				title="Instructions"
				content={task.instructions || ""}
				open={instructionsOpen}
				readOnly={task.status === TaskStatus.COMPLETED}
				onSave={(content) => save({ instructions: content })}
				onClose={() => setInstructionsOpen(false)}
			/>

			<AssetViewerDrawer
				asset={selectedAsset}
				onClose={() => setSelectedAsset(null)}
			/>

			<UploadAssetDialog
				open={uploadAssetOpen}
				onClose={() => setUploadAssetOpen(false)}
				parentId={taskId}
				parentType={ParentType.TASK}
				workspaceId={workspaceId}
				onUploadComplete={() =>
					queryClient.invalidateQueries({ queryKey: ["task", taskId] })
				}
			/>
			{dialog}
		</>
	);
}
